"use client";

import { SettingsSection } from "@commerce/components/page";
import { dateLabel, useNow, useTimeZone } from "@commerce/components/time";
import { emailSchema } from "@repo/api/lib/validation";
import type { ActiveOrganization } from "@repo/auth";
import { authClient } from "@repo/auth/client";
import { isOrganizationAdmin } from "@repo/auth/lib/helper";
import { useSession } from "@saas/auth/client";
import {
	fullOrganizationQueryOptions,
	organizationsQueryKeys,
} from "@saas/organizations/lib/api";
import { SettingsPageIntro } from "@saas/settings/components/SettingsLayout";
import { errorMessage } from "@saas/settings/lib/errors";
import { useConfirmationAlert } from "@saas/shared/components/ConfirmationAlertProvider";
import { Pager } from "@saas/shared/components/Pager";
import { UserAvatar } from "@shared/components/UserAvatar";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Badge } from "@ui/components/badge";
import { Button } from "@ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@ui/components/dropdown-menu";
import { Field, FieldLabel } from "@ui/components/field";
import { DisabledReason } from "@ui/components/info-tip";
import { Input } from "@ui/components/input";
import { Skeleton } from "@ui/components/skeleton";
import { Spinner } from "@ui/components/spinner";
import { toast } from "@ui/components/toast";
import { formatDistanceStrict } from "date-fns";
import {
	LogOutIcon,
	MailIcon,
	MoreHorizontalIcon,
	UserMinusIcon,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { ORGANIZATION_MEMBER_ROLES } from "../hooks/member-roles";
import { OrganizationRoleSelect } from "./OrganizationRoleSelect";

const PAGE_SIZE = 20;

const roleLabel = (role: string) =>
	ORGANIZATION_MEMBER_ROLES[role as keyof typeof ORGANIZATION_MEMBER_ROLES] ??
	role;

function useOrganizationDetail(organizationId: string) {
	const { data } = useSuspenseQuery(
		fullOrganizationQueryOptions(organizationId),
	);
	return data as ActiveOrganization | null;
}

export function TeamSettings({ organizationId }: { organizationId: string }) {
	const { user } = useSession();
	const organization = useOrganizationDetail(organizationId);
	const canManage = isOrganizationAdmin(organization, user);
	const invitations =
		organization?.invitations
			?.filter((invitation) => invitation.status === "pending")
			.sort(
				(a, b) =>
					new Date(a.expiresAt).getTime() -
					new Date(b.expiresAt).getTime(),
			) ?? [];

	return (
		<>
			<SettingsPageIntro
				title="Team"
				info="People who can sign in to this retailer's admin. Owners and admins change the agent, catalog and team; members can read everything and use the playground."
			/>
			{canManage ? (
				<InviteSection organizationId={organizationId} />
			) : null}
			<MembersSection
				organizationId={organizationId}
				organization={organization}
				canManage={canManage}
			/>
			{invitations.length > 0 ? (
				<InvitationsSection
					organizationId={organizationId}
					invitations={invitations}
					canManage={canManage}
				/>
			) : null}
		</>
	);
}

export function TeamSettingsSkeleton() {
	return (
		<div className="flex flex-col gap-6">
			<Skeleton className="h-10 w-2/3" />
			<div className="flex flex-col gap-4 rounded-3xl p-5 ring-1 ring-foreground/6">
				{Array.from({ length: 3 }).map((_, index) => (
					<div key={index} className="flex items-center gap-3">
						<Skeleton className="size-9 rounded-full" />
						<div className="flex flex-1 flex-col gap-1.5">
							<Skeleton className="h-4 w-40" />
							<Skeleton className="h-3 w-56" />
						</div>
						<Skeleton className="h-8 w-28" />
					</div>
				))}
			</div>
		</div>
	);
}

function useRefreshTeam(organizationId: string) {
	const queryClient = useQueryClient();
	return () =>
		queryClient.invalidateQueries({
			queryKey: organizationsQueryKeys.detail(organizationId),
		});
}

function InviteSection({ organizationId }: { organizationId: string }) {
	const refresh = useRefreshTeam(organizationId);
	const [email, setEmail] = useState("");
	const [role, setRole] = useState("member");
	const [error, setError] = useState<string | null>(null);
	const [sending, setSending] = useState(false);

	const submit = async (event: FormEvent) => {
		event.preventDefault();
		const parsed = emailSchema.safeParse(email.trim());
		if (!parsed.success) {
			setError("Enter a full email address, like name@retailer.com");
			return;
		}
		setSending(true);
		try {
			const { error } = await authClient.organization.inviteMember({
				email: email.trim(),
				// Better Auth accepts custom role names at runtime.
				role: role as "member" | "admin" | "owner",
				organizationId,
			});
			if (error) {
				throw error;
			}
			toast.add({
				type: "success",
				title: `Invitation sent to ${email.trim()}`,
			});
			setEmail("");
			setRole("member");
			await refresh();
		} catch (error) {
			toast.add({
				type: "error",
				title: "Could not send the invitation",
				description: errorMessage(error),
			});
		} finally {
			setSending(false);
		}
	};

	return (
		<SettingsSection
			title="Invite someone"
			info="They get an email with a link to join. Invite as Member unless they need to change the agent or catalog."
		>
			<form onSubmit={submit} className="flex flex-col gap-3 py-4">
				<Field data-invalid={error ? true : undefined}>
					<FieldLabel htmlFor="invite-email" className="sr-only">
						Email
					</FieldLabel>
					<div className="flex flex-col gap-2 sm:flex-row">
						<Input
							id="invite-email"
							type="email"
							placeholder="name@retailer.com"
							value={email}
							onChange={(event) => {
								setEmail(event.target.value);
								setError(null);
							}}
							aria-invalid={error ? true : undefined}
							className="flex-1"
						/>
						<OrganizationRoleSelect
							value={role}
							onSelect={setRole}
							organizationId={organizationId}
							className="sm:w-32"
						/>
						<DisabledReason
							reason={
								!sending &&
								!email.trim() &&
								"Enter an email first"
							}
						>
							<Button
								type="submit"
								disabled={sending || !email.trim()}
								className="w-full sm:w-auto"
							>
								{sending ? (
									<Spinner data-icon="inline-start" />
								) : (
									<MailIcon data-icon="inline-start" />
								)}
								Send invite
							</Button>
						</DisabledReason>
					</div>
					{error ? (
						<p role="alert" className="text-destructive text-sm">
							{error}
						</p>
					) : null}
				</Field>
			</form>
		</SettingsSection>
	);
}

function MembersSection({
	organizationId,
	organization,
	canManage,
}: {
	organizationId: string;
	organization: ActiveOrganization | null;
	canManage: boolean;
}) {
	const { user } = useSession();
	const { confirm } = useConfirmationAlert();
	const refresh = useRefreshTeam(organizationId);
	const [page, setPage] = useState(1);
	const [updating, setUpdating] = useState<string | null>(null);
	const members = organization?.members ?? [];
	const visible = members.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	const changeRole = async (memberId: string, name: string, role: string) => {
		setUpdating(memberId);
		try {
			const { error } = await authClient.organization.updateMemberRole({
				memberId,
				role,
				organizationId,
			});
			if (error) {
				throw error;
			}
			await refresh();
			toast.add({
				type: "success",
				title: `${name} is now ${roleLabel(role)}`,
			});
		} catch (error) {
			toast.add({
				type: "error",
				title: "Could not change the role",
				description: errorMessage(error),
			});
		} finally {
			setUpdating(null);
		}
	};

	const remove = (memberId: string, name: string, self: boolean) =>
		confirm({
			title: self ? `Leave ${organization?.name}?` : `Remove ${name}?`,
			message: self
				? "You lose access to this retailer's admin right away. An owner or admin has to invite you again to come back."
				: `${name} loses access to this retailer's admin right away. Their past changes stay in the audit log.`,
			confirmLabel: self ? "Leave retailer" : "Remove from team",
			destructive: true,
			onConfirm: async () => {
				const { error } = await authClient.organization.removeMember({
					memberIdOrEmail: memberId,
					organizationId,
				});
				if (error) {
					throw error;
				}
				if (self) {
					window.location.href = "/app";
					return;
				}
				await refresh();
				toast.add({ type: "success", title: `${name} was removed` });
			},
		});

	return (
		<SettingsSection
			title={`Members (${members.length})`}
			info="Owners can't be demoted or removed here; ask Hephon to transfer ownership."
		>
			<ul className="flex flex-col divide-y divide-border/70">
				{visible.map((member) => {
					const self = member.userId === user?.id;
					const name =
						member.user?.name || member.user?.email || "Member";
					const isOwner = member.role === "owner";
					return (
						<li
							key={member.id}
							className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3"
						>
							<UserAvatar
								name={name}
								avatarUrl={member.user?.image}
								className="size-9"
							/>
							<div className="min-w-0 flex-1">
								<p className="flex items-center gap-2 truncate font-medium text-sm">
									<span className="truncate">{name}</span>
									{self ? (
										<Badge variant="secondary">You</Badge>
									) : null}
								</p>
								<p className="truncate text-muted-foreground text-xs">
									{member.user?.email}
								</p>
							</div>
							<div className="flex items-center gap-1 max-sm:w-full max-sm:pl-12">
								{canManage && !isOwner && !self ? (
									<div className="flex items-center gap-2">
										{updating === member.id ? (
											<Spinner />
										) : null}
										<OrganizationRoleSelect
											value={member.role}
											onSelect={(role) =>
												role !== member.role &&
												changeRole(
													member.id,
													name,
													role,
												)
											}
											disabled={updating !== null}
											organizationId={organizationId}
											aria-label={`Role for ${name}`}
										/>
									</div>
								) : (
									<span className="px-3 text-muted-foreground text-sm">
										{roleLabel(member.role)}
									</span>
								)}
								{(canManage && !self && !isOwner) ||
								(self && !isOwner) ? (
									<DropdownMenu>
										<DropdownMenuTrigger
											render={
												<Button
													variant="ghost"
													size="icon-sm"
													aria-label={`More for ${name}`}
												/>
											}
										>
											<MoreHorizontalIcon />
										</DropdownMenuTrigger>
										<DropdownMenuContent align="end">
											{self ? (
												<DropdownMenuItem
													variant="destructive"
													onClick={() =>
														remove(
															member.id,
															name,
															true,
														)
													}
												>
													<LogOutIcon />
													Leave retailer
												</DropdownMenuItem>
											) : (
												<DropdownMenuItem
													variant="destructive"
													onClick={() =>
														remove(
															member.id,
															name,
															false,
														)
													}
												>
													<UserMinusIcon />
													Remove from team
												</DropdownMenuItem>
											)}
										</DropdownMenuContent>
									</DropdownMenu>
								) : (
									<span className="size-7" aria-hidden />
								)}
							</div>
						</li>
					);
				})}
			</ul>
			{members.length > PAGE_SIZE ? (
				<div className="py-3">
					<Pager
						page={page}
						pageSize={PAGE_SIZE}
						total={members.length}
						onPageChange={setPage}
					/>
				</div>
			) : null}
		</SettingsSection>
	);
}

function InvitationsSection({
	organizationId,
	invitations,
	canManage,
}: {
	organizationId: string;
	invitations: ActiveOrganization["invitations"];
	canManage: boolean;
}) {
	const { confirm } = useConfirmationAlert();
	const refresh = useRefreshTeam(organizationId);

	return (
		<SettingsSection title={`Waiting to join (${invitations.length})`}>
			<ul className="flex flex-col divide-y divide-border/70">
				{invitations.map((invitation) => (
					<InvitationRow
						key={invitation.id}
						invitation={invitation}
						canManage={canManage}
						onCancel={() =>
							confirm({
								title: `Cancel the invitation for ${invitation.email}?`,
								message:
									"The link in their email stops working. You can invite them again later.",
								confirmLabel: "Cancel invitation",
								cancelLabel: "Keep it",
								destructive: true,
								onConfirm: async () => {
									const { error } =
										await authClient.organization.cancelInvitation(
											{
												invitationId: invitation.id,
											},
										);
									if (error) {
										throw error;
									}
									await refresh();
									toast.add({
										type: "success",
										title: "Invitation cancelled",
									});
								},
							})
						}
					/>
				))}
			</ul>
		</SettingsSection>
	);
}

/**
 * "expires in 3 days" depends on now, so it appears once the page is live (`useNow` is 0
 * on the server and while hydrating); until then the row shows the date, in the panel's
 * timezone, so both renders agree.
 */
function InvitationRow({
	invitation,
	canManage,
	onCancel,
}: {
	invitation: ActiveOrganization["invitations"][number];
	canManage: boolean;
	onCancel: () => void;
}) {
	const timeZone = useTimeZone();
	const now = useNow();
	const expires = new Date(invitation.expiresAt);
	const expired = now > 0 && expires.getTime() < now;
	const expiry = now
		? formatDistanceStrict(expires, now, { addSuffix: true })
		: dateLabel(expires, timeZone, true);

	return (
		<li className="flex items-center gap-3 py-3">
			<span className="grid size-9 shrink-0 place-items-center rounded-full border border-border border-dashed text-muted-foreground">
				<MailIcon className="size-4" aria-hidden />
			</span>
			<div className="min-w-0 flex-1">
				<p className="truncate text-sm">{invitation.email}</p>
				<p className="truncate text-muted-foreground text-xs">
					Invited as {roleLabel(invitation.role)} ·{" "}
					{expired ? "expired" : `expires ${expiry}`}
				</p>
			</div>
			{expired ? <Badge variant="warning">Expired</Badge> : null}
			{canManage ? (
				<Button variant="ghost" size="sm" onClick={onCancel}>
					Cancel
				</Button>
			) : null}
		</li>
	);
}
